import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Navigation from './components/Navigation';
import Movement from './components/Movement';
import Replay from './components/Veritas';
import { Canvas } from '@react-three/fiber'
import { Suspense, useState, useEffect, useRef } from 'react';
import CrystalBall from './components/3d/CrystalBall'
import Phantom from './components/Phantom';
import Pricing from './components/Pricing';
import About from './components/About';

import Veritas from './components/Veritas';
import Archangel from './components/Archangel';
import Coverage from './components/Coverage';
import './orchestra-animations.css'; // Import the CSS file

// Add this function at the top of your file, outside the component
function isMobileDevice() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Check for iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;

  // Check for Android devices
  const isAndroid = /android/i.test(userAgent);

  return isIOS || isAndroid;
}

// Create a sophisticated loader component
const OrchestraLoader = ({ onSoundToggle, isSoundOn }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 0.5;
        if (newProgress >= 100) {
          clearInterval(timer);
          return 100;
        }
        return newProgress;
      });
    }, 20);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
      {/* Elegant branding */}
      <div className="mb-16">
        <div className="text-base font-light tracking-[0.4em] mb-3 text-white/90">
          <span className="bg-gradient-to-r from-white/90 via-white to-white/90 text-transparent bg-clip-text">
            ORCHESTRA
          </span>
        </div>
        <div className="text-3xl font-extralight tracking-[0.15em] text-white/80">
          Tuning
        </div>
      </div>

      {/* Haute couture loading indicator */}
      <div className="relative w-64 mb-20">
        {/* Elegant thin line */}
        <div className="w-full h-[1px] bg-white/10"></div>

        {/* Golden ratio marker lines */}
        <div className="absolute top-0 left-[38.2%] h-3 w-[1px] bg-white/20 transform -translate-y-1"></div>
        <div className="absolute top-0 left-[61.8%] h-3 w-[1px] bg-white/20 transform -translate-y-1"></div>

        {/* Sophisticated progress indicator */}
        <div
          className="absolute top-0 left-0 h-[1px] bg-gradient-to-r from-white/40 via-white to-white/40 transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        ></div>

        {/* Elegant progress dot */}
        <div
          className="absolute top-0 w-1.5 h-1.5 rounded-full bg-white transition-all duration-300 ease-out transform -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${progress}%` }}
        ></div>

        {/* Minimalist musical notation */}
        {[15, 30, 45, 60, 75, 90].map((position, i) => (
          <div
            key={i}
            className={`absolute top-4 transition-opacity duration-700 ease-in-out ${progress >= position ? 'opacity-100' : 'opacity-0'}`}
            style={{
              left: `${position}%`,
              transform: 'translateX(-50%)',
              transitionDelay: `${i * 100}ms`
            }}
          >
            <div className="text-white/80 font-serif italic text-sm">
              {['p', 'mf', 'f', 'mp', 'pp', 'ff'][i]}
            </div>
          </div>
        ))}

        {/* Percentage counter */}
        <div className="absolute top-8 left-1/2 transform -translate-x-1/2 text-white/60 text-xs tracking-widest font-light">
          {Math.round(progress)}
        </div>
      </div>

      {/* Abstract orchestra visualization */}
      <div className="relative w-40 h-40 mb-16">
        {/* Concentric circles representing sound waves */}
        <div className="absolute inset-0 rounded-full border border-white/5"></div>
        <div className="absolute inset-[4px] rounded-full border border-white/5"></div>
        <div className="absolute inset-[8px] rounded-full border border-white/5"></div>
        <div className="absolute inset-[12px] rounded-full border border-white/5"></div>
        <div className="absolute inset-[16px] rounded-full border border-white/5"></div>

        {/* Animated elements */}
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Conductor's baton visualization */}
          <div className="relative w-20 h-20">
            {/* Animated golden ratio spiral */}
            <svg viewBox="0 0 100 100" className="w-full h-full opacity-60">
              <path
                d="M50,50 L50,20 A30,30 0 0,1 80,50 A30,30 0 0,1 50,80 A30,30 0 0,1 20,50 A30,30 0 0,1 50,20"
                fill="none"
                stroke="white"
                strokeWidth="0.5"
                strokeDasharray="126"
                strokeDashoffset={126 - (126 * progress / 100)}
                className="transition-all duration-300 ease-out"
              />
            </svg>

            {/* Pulsing center point */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-1 bg-white rounded-full animate-pulse"></div>

            {/* Animated dots representing orchestra members */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <div
                key={i}
                className={`absolute w-1 h-1 bg-white rounded-full transition-opacity duration-500 ${progress > i * 12 ? 'opacity-100' : 'opacity-0'}`}
                style={{
                  top: `calc(50% + ${Math.sin(angle * Math.PI / 180) * 30}%)`,
                  left: `calc(50% + ${Math.cos(angle * Math.PI / 180) * 30}%)`,
                  animationDelay: `${i * 0.2}s`,
                  animation: `pulseDelayed 3s infinite ${i * 0.2}s`
                }}
              ></div>
            ))}
          </div>
        </div>
      </div>

      {/* Refined sound control */}
      <div className="flex items-center">
        <button
          onClick={onSoundToggle}
          className="group flex items-center space-x-3 px-4 py-2 rounded-full transition-all duration-300"
        >
          <span className="text-white/60 text-xs tracking-[0.2em] group-hover:text-white/80 transition-colors duration-300">
            {isSoundOn ? "SYMPHONY PLAYING" : "ENABLE SYMPHONY"}
          </span>
          <div className={`relative w-5 h-5 ${isSoundOn ? 'opacity-90' : 'opacity-60'} group-hover:opacity-100 transition-opacity duration-300`}>
            {isSoundOn ? (
              // Sound waves icon
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                <path d="M8 9L8 15" stroke="white" strokeWidth="0.75" strokeLinecap="round">
                  <animate attributeName="d" values="M8 9L8 15; M8 7L8 17; M8 9L8 15" dur="2s" repeatCount="indefinite" />
                </path>
                <path d="M12 7L12 17" stroke="white" strokeWidth="0.75" strokeLinecap="round">
                  <animate attributeName="d" values="M12 7L12 17; M12 5L12 19; M12 7L12 17" dur="1.5s" repeatCount="indefinite" />
                </path>
                <path d="M16 10L16 14" stroke="white" strokeWidth="0.75" strokeLinecap="round">
                  <animate attributeName="d" values="M16 10L16 14; M16 6L16 18; M16 10L16 14" dur="1.8s" repeatCount="indefinite" />
                </path>
              </svg>
            ) : (
              // Muted icon
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                <path d="M8 9L8 15" stroke="white" strokeWidth="0.75" strokeLinecap="round" />
                <path d="M12 9L12 15" stroke="white" strokeWidth="0.75" strokeLinecap="round" />
                <path d="M16 9L16 15" stroke="white" strokeWidth="0.75" strokeLinecap="round" />
                <path d="M20 9L4 15" stroke="white" strokeWidth="0.75" strokeLinecap="round" />
              </svg>
            )}
          </div>
        </button>
      </div>
    </div>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSoundOn, setIsSoundOn] = useState(true);
  const [userInteracted, setUserInteracted] = useState(false);
  const audioRef = useRef(null);

  // Use our sophisticated mobile detection
  const isMobile = isMobileDevice();

  // Auto-interact on mobile devices to skip the initial screen
  useEffect(() => {
    if (isMobile) {
      // Automatically set userInteracted to true on mobile
      setUserInteracted(true);
      // Skip loading screen entirely on mobile
      setIsLoading(false);
    }
  }, [isMobile]);

  // Initialize audio but don't play yet - disable on mobile
  useEffect(() => {
    // Skip audio initialization on mobile
    if (isMobile) {
      setIsSoundOn(false);
      return;
    }

    audioRef.current = new Audio('/audio/theme.mp3');
    audioRef.current.loop = true;
    audioRef.current.volume = 0.4;

    // Expose the audio reference globally for child components to access
    window.orchThemeAudio = audioRef.current;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        // Also remove the global reference
        window.orchThemeAudio = null;
      }
    };
  }, [isMobile]);

  // Play audio when user interacts - skip on mobile
  useEffect(() => {
    if (!isMobile && userInteracted && isSoundOn && audioRef.current) {
      audioRef.current.play()
        .then(() => console.log("Audio playing successfully"))
        .catch(e => {
          console.log("Audio play failed:", e);
          setIsSoundOn(false);
        });
    }
  }, [userInteracted, isSoundOn, isMobile]);

  // Start loading animation after user interaction - only on desktop
  useEffect(() => {
    if (userInteracted && !isMobile) {
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [userInteracted, isMobile]);

  // Handle sound toggle
  const handleSoundToggle = () => {
    if (!audioRef.current) return;

    if (isSoundOn) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(e => console.log("Audio play failed:", e));
    }

    setIsSoundOn(!isSoundOn);
  };

  // Different content for mobile vs desktop
  const MainContent = () => {
    // For mobile, render a simple static background instead of the 3D crystal ball
    if (isMobile) {
      return (
        <div
          style={{
            backgroundColor: '#000000',
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'auto'
          }}
        >
          <div className="w-full h-full bg-gradient-to-b from-black/50 via-black/30 to-transparent pointer-events-none" />
          <div className="w-full text-center text-white pt-40 pb-20">
            <div className="text-base font-light tracking-[0.25em] mb-4">
              <span className="bg-gradient-to-r from-white via-white/95 to-white text-transparent bg-clip-text 
                inline-block border-b border-white/20">
                ORCHESTRA PRESENTS
              </span>
            </div>
            <h1 className="text-6xl font-light tracking-wider mb-6 drop-shadow-lg">
              A live crystal ball of San Francisco
            </h1>
            <div className="space-y-3">
              <p className="text-2xl tracking-widest font-light drop-shadow-md">
                See everything. Be everywhere.
              </p>
              <div className="mt-12">
                <Link
                  to="/archangel"
                  className="group relative inline-block overflow-hidden px-14 py-6 rounded-2xl
                  bg-black/30 backdrop-blur-sm
                  border border-white/10
                  transition-all duration-500 
                  hover:border-white/20
                  hover:bg-black/40"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent 
                    transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000">
                  </div>

                  <div className="relative">
                    <div className="text-base font-light tracking-[0.25em] text-white/60 mb-2">
                      OPENING ACT
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-4xl font-light tracking-wider 
                        animate-gradient-text-hover
                        bg-gradient-to-r from-white via-white to-white/90
                        group-hover:animate-gradient-text
                        text-transparent bg-clip-text transition-all duration-700">
                        Archangel
                      </span>
                      <span className="text-2xl
                        animate-gradient-text-hover
                        bg-gradient-to-r from-white via-white to-white/90
                        group-hover:animate-gradient-text
                        text-transparent bg-clip-text transition-all duration-700">
                        →
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
          {/* Static gradient background instead of 3D crystal ball */}
          <div className="fixed top-0 left-0 w-full h-screen bg-gradient-radial from-gray-900 to-black -z-10"></div>
        </div>
      );
    }

    // For desktop, render the original content with 3D crystal ball
    return (
      <div
        style={{
          backgroundColor: '#000000',
          position: 'fixed',
          width: '100%',
          height: '100%',
          top: 0,
          left: 0,
          overflow: 'hidden'
        }}
      >
        <div className="absolute z-[5] w-full h-full bg-gradient-to-b from-black/50 via-black/30 to-transparent pointer-events-none" />
        <div className="absolute z-10 w-full text-center text-white pt-40">
          <div className="text-base font-light tracking-[0.25em] mb-4">
            <span className="bg-gradient-to-r from-white via-white/95 to-white text-transparent bg-clip-text 
              inline-block border-b border-white/20">
              ORCHESTRA PRESENTS
            </span>
          </div>
          <h1 className="text-6xl font-light tracking-wider mb-6 drop-shadow-lg">
            A live crystal ball of San Francisco
          </h1>
          <div className="space-y-3">
            <p className="text-2xl tracking-widest font-light drop-shadow-md">
              See everything. Be everywhere.
            </p>
            <div className="mt-12">
              <Link
                to="/archangel"
                className="group relative inline-block overflow-hidden px-14 py-6 rounded-2xl
                bg-black/30 backdrop-blur-sm
                border border-white/10
                transition-all duration-500 
                hover:border-white/20
                hover:bg-black/40"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent 
                  transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000">
                </div>

                <div className="relative">
                  <div className="text-base font-light tracking-[0.25em] text-white/60 mb-2">
                    OPENING ACT
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-4xl font-light tracking-wider 
                      animate-gradient-text-hover
                      bg-gradient-to-r from-white via-white to-white/90
                      group-hover:animate-gradient-text
                      text-transparent bg-clip-text transition-all duration-700">
                      Archangel
                    </span>
                    <span className="text-2xl
                      animate-gradient-text-hover
                      bg-gradient-to-r from-white via-white to-white/90
                      group-hover:animate-gradient-text
                      text-transparent bg-clip-text transition-all duration-700">
                      →
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute top-0 left-0 w-full h-screen">
          <Canvas
            camera={{ position: [0, 0, 75], fov: 60 }}
            gl={{
              preserveDrawingBuffer: true,
              antialias: true
            }}
          >
            <Suspense fallback={null}>
              <CrystalBall />
            </Suspense>
          </Canvas>
        </div>
      </div>
    );
  };

  return (
    <Router>
      <div className="min-h-screen bg-black">
        {!userInteracted && !isMobile ? (
          // Elegant entry point requiring user interaction - ONLY shown on desktop
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
            <div className="mb-16">
              <div className="text-base font-light tracking-[0.4em] mb-3 text-white/90">
                <span className="bg-gradient-to-r from-white/90 via-white to-white/90 text-transparent bg-clip-text">
                  ORCHESTRA
                </span>
              </div>
              <div className="text-3xl font-extralight tracking-[0.15em] text-white/80">
                Composing reality
              </div>
            </div>

            <button
              onClick={() => setUserInteracted(true)}
              className="px-8 py-3 border border-white/10 rounded-full bg-white/5 hover:bg-white/10 
                transition-all duration-500 group"
            >
              <span className="text-sm font-light tracking-[0.3em] text-white/70 group-hover:text-white/90">
                BEGIN EXPERIENCE
              </span>
            </button>
          </div>
        ) : isLoading && !isMobile ? (
          // Only show loader on desktop
          <OrchestraLoader
            onSoundToggle={handleSoundToggle}
            isSoundOn={isSoundOn}
          />
        ) : (
          // Main content
          <>
            {/* Sound control - only on desktop */}
            {!isMobile && (
              <div className="fixed bottom-6 right-6 z-50">
                <button
                  onClick={handleSoundToggle}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm 
                    border border-white/10 hover:border-white/20 transition-all"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {isSoundOn ? (
                      <path d="M11 5L6 9H2V15H6L11 19V5Z M15.54 8.46C16.4774 9.39764 17.004 10.6692 17.004 11.995C17.004 13.3208 16.4774 14.5924 15.54 15.53"
                        stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                    ) : (
                      <path d="M11 5L6 9H2V15H6L11 19V5Z M23 9L17 15 M17 9L23 15"
                        stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                    )}
                  </svg>
                </button>
              </div>
            )}
            <Navigation />
            <main className="h-full">
              <Routes>
                <Route path="/" element={<MainContent />} />
                <Route path="/movement" element={<Movement />} />
                <Route path="/phantom" element={<Phantom />} />
                <Route path="/replay" element={<Replay />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/about" element={<About />} />
                <Route path="/veritas" element={<Veritas />} />
                <Route path="/archangel" element={<Archangel />} />
                <Route path="/coverage" element={<Coverage />} />
              </Routes>
            </main>
          </>
        )}
      </div>
    </Router>
  );
}

export default App;





